import {
  Button,
  breakpoints,
  Col,
  Dialog,
  DropdownMenu,
  HoverRegion,
  Icon,
  type IconName,
  Input,
  type MenuItem,
  onWeb,
  Row,
  ScrollArea,
  Tappable,
  Text,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { InviteBox } from "../../invites/invite-box.tsx";
import { failureSaid } from "../../localization/failure.ts";
import { say } from "../../localization/say.ts";
import { WhenLabel } from "../../localization/when-label.tsx";
import { act, read } from "../../net/rally.ts";
import { NotificationsCard } from "../../push/card.tsx";
import { goTo } from "../../router.ts";
import { coalesced } from "../activity-refresh.ts";
import { countsOf, type FilterName, inOrder, inState, isFlat } from "../find/sift.ts";
import { Sifter } from "../find/sifter.tsx";
import { AccountEntries } from "../person/account-entries.tsx";
import { ProjectRow } from "../project-row.tsx";
import { useLive } from "../run/live.ts";
import { useSeen } from "../seen.ts";
import { snoozeOffers, whenItMeans } from "../snooze.ts";
import { ConversationIcon } from "./conversation-icon.tsx";
import { previewConversations } from "./preview-conversations.ts";
import { type Somebody, titleOf } from "./title.ts";

type PanelPerson = Somebody & { avatar: string | null };

type PanelConversation = {
  id: string;
  title: string | null;

  icon: string | null;
  lastAt: string | null;
  mine: boolean;
  pinnedAt: string | null;

  people: PanelPerson[];

  unread: number;

  muted: boolean;

  snoozedUntil: string | null;

  archived: boolean;

  hasAgent: boolean;
};

type PanelProject = {
  id: string;
  name: string;
  icon: string | null;
  image: string | null;
  mark: string | null;
  conversations: PanelConversation[];
};

type WhereIAm = { me: { account: string }; projects: PanelProject[]; personal: PanelConversation[] };

export const PERSONAL = "personal";

const A_LABEL_AND_ITS_ROOMS_ARE_APART_BY = 4;

function aGroup(theme: ReturnType<typeof useTheme>) {
  return { marginTop: theme.space[3] };
}

function toggled(was: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(was);
  if (!next.delete(id)) next.add(id);
  return next;
}

export function Column({ at, projectAt }: { at: string | null; projectAt?: string | null }) {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);
  const [where, setWhere] = useState<WhereIAm | null>(null);

  const [naming, setNaming] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!onWeb || typeof document === "undefined") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const at = document.activeElement as HTMLElement | null;
      const list = document.querySelector('[data-testid="list-scroll"]');
      if (!at || !list?.contains(at)) return;
      if (at.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(at.tagName)) return;
      const rows = Array.from(
        list.querySelectorAll<HTMLElement>('[data-testid^="conversation-"][role="link"]'),
      ).filter((row) => !row.getAttribute("data-testid")?.startsWith("conversation-more-"));
      if (rows.length === 0 || !rows.some((row) => row === at)) return;
      event.preventDefault();
      const here = rows.findIndex((row) => row === at || row.contains(at));
      const next = event.key === "ArrowDown" ? Math.min(rows.length - 1, here + 1) : Math.max(0, here - 1);
      rows[next]?.focus();
      rows[next]?.scrollIntoView({ block: "nearest" });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const [name, setName] = useState("");

  const [inviting, setInviting] = useState<PanelProject | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);

  const [filter, setFilter] = useState<FilterName>("all");

  const [unreadFirst, setUnreadFirst] = useState(false);

  const again = useCallback(() => {
    read<WhereIAm>("where.i.am")
      .then(setWhere)
      .catch(() => undefined);
  }, []);
  useEffect(again, [again]);
  const refresh = useMemo(() => coalesced(again), [again]);
  useEffect(() => () => refresh.cancel(), [refresh]);

  const startPersonal = async (): Promise<void> => {
    setCreationError(null);
    try {
      const made = await act<{ id: string }>("conversation.create", {});
      again();
      goTo(`/c/${made.id}`);
    } catch (cause) {
      setCreationError(failureSaid(cause, say("chat.createFailed")));
    }
  };

  const startIn = async (project: string): Promise<void> => {
    setCreationError(null);
    try {
      const made = await act<{ conversation: string }>("project.addConversation", { project });
      again();
      goTo(`/c/${made.conversation}`);
    } catch (cause) {
      setCreationError(failureSaid(cause, say("chat.createFailed")));
    }
  };

  const makeProject = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed || creatingProject) return;
    setCreatingProject(true);
    setProjectError(null);
    try {
      const made = await act<{ id: string }>("project.create", { name: trimmed });
      setName("");
      setNaming(false);
      again();
      goTo(`/p/${made.id}`);
    } catch (cause) {
      setProjectError(failureSaid(cause, say("project.createFailed")));
    } finally {
      setCreatingProject(false);
    }
  };

  useLive((notice) => {
    if (notice.conversation || notice.project) refresh.notice();
  });

  useSeen(refresh.notice);

  const me = where?.me.account ?? "";

  const everything = useMemo(() => {
    const by = new Map<string, PanelConversation>();
    for (const one of [
      ...(where?.personal ?? []),
      ...(where?.projects ?? []).flatMap((project) => project.conversations),
    ]) {
      if (!by.has(one.id)) by.set(one.id, one);
    }
    return [...by.values()];
  }, [where]);

  const counts = useMemo(() => countsOf(everything), [everything]);

  const sifting = filter !== "all";

  const shown = (list: PanelConversation[]): PanelConversation[] =>
    inOrder(
      list.filter((one) => inState(one, filter)),
      unreadFirst,
    );

  const rows = (list: PanelConversation[]) =>
    list.map((one) => {
      const here = one.id === at;

      const quiet = one.muted || one.snoozedUntil !== null;
      const unread = one.unread > 0;

      const target = { kind: "conversation", id: one.id };
      const attend = (name: string, body: object) => {
        void act(name, { target, ...body })
          .then(again)
          .catch(() => {});
      };
      const menu: MenuItem[] = [
        {
          label: say(one.pinnedAt ? "actions.unpin" : "actions.pin"),
          icon: "pin",
          onPress: () => attend(one.pinnedAt ? "attention.unpin" : "attention.pin", {}),
        },
        one.snoozedUntil
          ? {
              label: say("actions.unsnooze"),
              icon: "alarm-clock",
              onPress: () => attend("attention.unsnooze", {}),
            }
          : {
              label: say("actions.snooze"),
              icon: "alarm-clock",
              submenu: snoozeOffers(new Date()).map((offer) => ({
                label: offer.label,
                hint: whenItMeans(offer.until, new Date()),
                onPress: () => attend("attention.snooze", { until: offer.until }),
              })),
            },
        {
          label: say(one.archived ? "actions.unarchive" : "actions.archive"),
          icon: "archive",
          onPress: () => attend(one.archived ? "attention.unarchive" : "attention.archive", {}),
        },
        ...(where && where.projects.length > 0
          ? [
              {
                label: say("sidebar.addToProject"),
                icon: "folder" as IconName,
                submenu: where.projects.map((project) => ({
                  label: project.name,
                  disabled: project.conversations.some((held) => held.id === one.id),
                  onPress: () => {
                    void act("project.addConversation", { project: project.id, conversation: one.id })
                      .then(again)
                      .catch(() => {});
                  },
                })),
              },
            ]
          : []),
      ];
      const marks: Array<{ name: IconName; what: string }> = [
        ...(one.pinnedAt ? [{ name: "pin" as IconName, what: say("sift.markPinned") }] : []),
        ...(one.snoozedUntil ? [{ name: "alarm-clock" as IconName, what: say("sift.markSnoozed") }] : []),
        ...(one.muted ? [{ name: "volume-off" as IconName, what: say("sift.markMuted") }] : []),
      ];
      return (
        <HoverRegion key={one.id}>
          {(hovered) => (
            <Tappable
              onPress={() => goTo(`/c/${one.id}`)}
              accessibilityRole="link"
              testID={`conversation-${one.id}`}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: theme.space[2],
                paddingVertical: theme.space[2],
                paddingRight: theme.space[2],
                paddingLeft: theme.space[2],
                borderRadius: theme.radius.md,
                backgroundColor: "transparent",
              }}
            >
              {here ? (
                <Col
                  style={{
                    position: "absolute",
                    left: 0,
                    top: theme.space[2] + 2,
                    width: 2,
                    height: theme.type.label.line - 2,
                    borderRadius: theme.radius.round,
                    backgroundColor: theme.color.accent,
                  }}
                />
              ) : null}
              <Col style={{ height: theme.type.label.line, justifyContent: "center" }}>
                <ConversationIcon
                  icon={one.icon}
                  title={one.title}
                  size={18}
                  color={here ? "accent" : "text.tertiary"}
                />
              </Col>
              <Col grow>
                <Row gap={2} align="center">
                  <Text
                    typeName="label"
                    color={here ? "accent" : unread && !quiet ? "text.primary" : "text.secondary"}
                    numberOfLines={1}
                    style={{ flex: 1, fontWeight: unread && !quiet ? "600" : "500" }}
                  >
                    {titleOf(one, me)}
                  </Text>
                  {marks.map((mark) => (
                    <Col key={mark.name} testID={`mark-${mark.name}-${one.id}`}>
                      <Icon name={mark.name} size={12} color="text.tertiary" />
                    </Col>
                  ))}
                  {hovered || here ? (
                    <DropdownMenu
                      items={menu}
                      align="right"
                      trigger={
                        <Col testID={`conversation-more-${one.id}`}>
                          <Icon name="more-horizontal" size={14} color="text.tertiary" />
                        </Col>
                      }
                    />
                  ) : null}
                  {unread ? (
                    <Text
                      testID={`unread-${one.id}`}
                      typeName="caption"
                      color={one.muted ? "text.tertiary" : "accent"}
                    >
                      {String(one.unread)}
                    </Text>
                  ) : null}
                  <Col testID={`conversation-time-${one.id}`}>
                    {one.lastAt && Number.isFinite(Date.parse(one.lastAt)) ? (
                      <WhenLabel at={one.lastAt} compact />
                    ) : (
                      <Text typeName="caption" color="text.tertiary">
                        {say("project.noDate")}
                      </Text>
                    )}
                  </Col>
                </Row>
              </Col>
            </Tappable>
          )}
        </HoverRegion>
      );
    });

  const groupRows = (id: string, held: PanelConversation[]) =>
    rows(previewConversations(held, expanded.has(id), at));

  const list = (
    <Col gap={3} grow>
      <NotificationsCard />

      <AccountEntries />

      <Sifter
        counts={counts}
        filter={filter}
        onFilter={setFilter}
        unreadFirst={unreadFirst}
        onUnreadFirst={setUnreadFirst}
        onNewProject={() => {
          setProjectError(null);
          setName("");
          setNaming(true);
        }}
      />

      {isFlat(filter) ? (
        <Col gap={1} testID="sift-flat">
          {rows(shown(everything))}
        </Col>
      ) : (
        <>
          {sifting && shown(where?.personal ?? []).length === 0 ? null : (
            <Col gap={1} style={aGroup(theme)}>
              <ProjectRow
                id={PERSONAL}
                name={say("chat.personal")}
                icon="user"
                here={projectAt === PERSONAL}
                onOpen={() => goTo(`/p/${PERSONAL}`)}
                onAdd={() => void startPersonal()}
                expanded={expanded.has(PERSONAL)}
                onToggle={
                  shown(where?.personal ?? []).length > 3
                    ? () => setExpanded((was) => toggled(was, PERSONAL))
                    : undefined
                }
              />
              {where ? (
                <Col gap={1} style={{ marginTop: A_LABEL_AND_ITS_ROOMS_ARE_APART_BY }}>
                  {groupRows(PERSONAL, shown(where.personal))}
                </Col>
              ) : null}
            </Col>
          )}

          {where?.projects.map((project) => {
            const held = shown(project.conversations);

            if (sifting && held.length === 0) return null;
            return (
              <Col gap={1} key={project.id} style={aGroup(theme)}>
                <ProjectRow
                  id={project.id}
                  name={project.name}
                  icon={project.icon}
                  image={project.image}
                  mark={project.mark}
                  here={project.id === projectAt}
                  onOpen={() => goTo(`/p/${project.id}`)}
                  onAdd={() => void startIn(project.id)}
                  expanded={expanded.has(project.id)}
                  onToggle={
                    held.length > 3 ? () => setExpanded((was) => toggled(was, project.id)) : undefined
                  }
                  onInvite={() => setInviting(project)}
                />
                <Col gap={1} style={{ marginTop: A_LABEL_AND_ITS_ROOMS_ARE_APART_BY }}>
                  {groupRows(project.id, held)}
                </Col>
              </Col>
            );
          })}
        </>
      )}

      <Dialog
        visible={naming}
        onDismiss={() => {
          if (!creatingProject) {
            setNaming(false);
            setName("");
            setProjectError(null);
          }
        }}
        title={say("chat.newProject")}
        testID="project-create"
      >
        <Input
          value={name}
          onChangeText={setName}
          placeholder={say("chat.projectName")}
          autoFocus
          testID="project-name"
          onSubmitEditing={() => void makeProject()}
        />
        {projectError ? (
          <Text accessibilityRole="alert" color="danger" testID="project-create-error">
            {projectError}
          </Text>
        ) : null}
        <Row gap={2}>
          <Button
            variant="ghost"
            disabled={creatingProject}
            onPress={() => {
              setNaming(false);
              setName("");
              setProjectError(null);
            }}
            testID="project-create-cancel"
          >
            {say("app.cancel")}
          </Button>
          <Button
            loading={creatingProject}
            disabled={!name.trim()}
            onPress={() => void makeProject()}
            testID="project-create-save"
          >
            {say("chat.newProject")}
          </Button>
        </Row>
      </Dialog>

      {inviting ? (
        <Dialog visible onDismiss={() => setInviting(null)} title={inviting.name} testID="project-invite">
          <InviteBox place={{ kind: "project", id: inviting.id, name: inviting.name }} />
        </Dialog>
      ) : null}
    </Col>
  );
  return (
    <Col grow style={{ minHeight: 0 }}>
      {creationError ? (
        <Text typeName="caption" color="danger" accessibilityRole="alert" testID="conversation-create-error">
          {creationError}
        </Text>
      ) : null}
      <ScrollArea
        testID="list-scroll"
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={{
          paddingBottom: theme.space[6],
          paddingHorizontal: narrow ? theme.space[3] : 0,
        }}
      >
        {list}
      </ScrollArea>
    </Col>
  );
}
